import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type MemberSpendLine,
  MemberSpendLineSchema,
  type WalletBusinessType,
  WalletBusinessTypeSchema,
  type WalletPaymentTargetType,
  WalletPaymentTargetTypeSchema,
  type WalletUnit,
  WalletUnitSchema,
} from "./commercial.js";

const SafeFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const SafeWalletAmountSchema = Type.Integer({
  minimum: Number.MIN_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const ConfigurationIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$",
});

export const MemberSpendPreviewRequestSchema = Type.Object(
  {
    memberId: Type.String({ minLength: 1, maxLength: 256 }),
    orderId: Type.String({ minLength: 1, maxLength: 256 }),
    amountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    paymentRuleId: Type.Optional(Type.Union([ConfigurationIdSchema, Type.Null()])),
    targetType: Type.Optional(WalletPaymentTargetTypeSchema),
    targetId: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    ),
    allowExternalShortfall: Type.Optional(Type.Boolean()),
    lines: Type.Optional(Type.Array(MemberSpendLineSchema, { minItems: 1, maxItems: 100 })),
  },
  { additionalProperties: false },
);
export type MemberSpendPreviewRequest = Static<typeof MemberSpendPreviewRequestSchema>;

export interface MemberSpendPreviewEntry {
  walletDefinitionId: string;
  walletName: string;
  unit: WalletUnit;
  customUnitLabel: string | null;
  precision: number;
  businessType: WalletBusinessType;
  balanceBefore: number;
  delta: number;
  balanceAfter: number;
  coveredAmountFen: number;
  refundableAtCheckout: boolean;
  refundDestination: "ORIGINAL_WALLET" | "NOT_REFUNDABLE";
}

export interface MemberSpendPreviewRuleApplication {
  targetType: WalletPaymentTargetType;
  targetId: string | null;
  amountFen: number;
  paymentRuleId: string;
  paymentRuleName: string;
  paymentRuleVersion: number;
  walletCoveredAmountFen: number;
  externalAmountFen: number;
}

export interface MemberSpendPreviewResult {
  contractVersion: "1.0.0";
  planHash: string;
  memberId: string;
  orderId: string;
  amountFen: number;
  memberVersion: number;
  walletVersion: number;
  walletCoveredAmountFen: number;
  externalAmountFen: number;
  entries: MemberSpendPreviewEntry[];
  ruleApplications: MemberSpendPreviewRuleApplication[];
  refundPolicy: {
    mode: "ORIGINAL_WALLETS_ONLY";
    refundableAmountFen: number;
    nonRefundableAmountFen: number;
    cashRefundAllowed: false;
  };
}

const MemberSpendPreviewEntrySchema = Type.Object(
  {
    walletDefinitionId: ConfigurationIdSchema,
    walletName: Type.String({ minLength: 1, maxLength: 40 }),
    unit: WalletUnitSchema,
    customUnitLabel: Type.Union([Type.String({ minLength: 1, maxLength: 12 }), Type.Null()]),
    precision: Type.Integer({ minimum: 0, maximum: 4 }),
    businessType: WalletBusinessTypeSchema,
    balanceBefore: SafeFenSchema,
    delta: SafeWalletAmountSchema,
    balanceAfter: SafeFenSchema,
    coveredAmountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    refundableAtCheckout: Type.Boolean(),
    refundDestination: Type.Union([
      Type.Literal("ORIGINAL_WALLET"),
      Type.Literal("NOT_REFUNDABLE"),
    ]),
  },
  { additionalProperties: false },
);

const MemberSpendPreviewRuleApplicationSchema = Type.Object(
  {
    targetType: WalletPaymentTargetTypeSchema,
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    paymentRuleId: ConfigurationIdSchema,
    paymentRuleName: Type.String({ minLength: 1, maxLength: 80 }),
    paymentRuleVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    walletCoveredAmountFen: SafeFenSchema,
    externalAmountFen: SafeFenSchema,
  },
  { additionalProperties: false },
);

export const MemberSpendPreviewResultSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    planHash: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    memberId: Type.String({ minLength: 1, maxLength: 256 }),
    orderId: Type.String({ minLength: 1, maxLength: 256 }),
    amountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    memberVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    walletVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    walletCoveredAmountFen: SafeFenSchema,
    externalAmountFen: SafeFenSchema,
    entries: Type.Array(MemberSpendPreviewEntrySchema, { minItems: 1, maxItems: 2_000 }),
    ruleApplications: Type.Array(MemberSpendPreviewRuleApplicationSchema, {
      minItems: 1,
      maxItems: 100,
    }),
    refundPolicy: Type.Object(
      {
        mode: Type.Literal("ORIGINAL_WALLETS_ONLY"),
        refundableAmountFen: SafeFenSchema,
        nonRefundableAmountFen: SafeFenSchema,
        cashRefundAllowed: Type.Literal(false),
      },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);

function safeSum(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

function spendLinesAreConsistent(lines: readonly MemberSpendLine[], amountFen: number): boolean {
  const identities = new Set<string>();
  for (const line of lines) {
    const identity = `${line.targetType}:${line.targetId ?? ""}`;
    if (
      identities.has(identity) ||
      (line.targetType === "ORDER" ? line.targetId !== null : line.targetId === null)
    ) {
      return false;
    }
    identities.add(identity);
  }
  return safeSum(lines.map((line) => line.amountFen)) === amountFen;
}

export function isMemberSpendPreviewRequest(value: unknown): value is MemberSpendPreviewRequest {
  if (!Value.Check(MemberSpendPreviewRequestSchema, value)) return false;
  const request = value as MemberSpendPreviewRequest;
  if (request.lines && (request.targetType !== undefined || request.targetId !== undefined)) {
    return false;
  }
  const lines = request.lines ?? [
    {
      targetType: request.targetType ?? "ORDER",
      targetId: request.targetId ?? null,
      amountFen: request.amountFen,
    },
  ];
  return (
    request.memberId === request.memberId.trim() &&
    request.orderId === request.orderId.trim() &&
    spendLinesAreConsistent(lines, request.amountFen)
  );
}

export function isMemberSpendPreviewResult(value: unknown): value is MemberSpendPreviewResult {
  if (!Value.Check(MemberSpendPreviewResultSchema, value)) return false;
  const result = value as MemberSpendPreviewResult;
  const walletIds = new Set<string>();
  let refundableAmountFen = 0;
  let nonRefundableAmountFen = 0;
  for (const entry of result.entries) {
    if (
      walletIds.has(entry.walletDefinitionId) ||
      entry.delta >= 0 ||
      entry.balanceAfter !== entry.balanceBefore + entry.delta ||
      (entry.unit === "CUSTOM") !== (entry.customUnitLabel !== null) ||
      entry.refundDestination !==
        (entry.refundableAtCheckout ? "ORIGINAL_WALLET" : "NOT_REFUNDABLE")
    ) {
      return false;
    }
    walletIds.add(entry.walletDefinitionId);
    if (entry.refundableAtCheckout) refundableAmountFen += entry.coveredAmountFen;
    else nonRefundableAmountFen += entry.coveredAmountFen;
  }
  const applicationAmountFen = safeSum(
    result.ruleApplications.map((application) => application.amountFen),
  );
  const applicationWalletFen = safeSum(
    result.ruleApplications.map((application) => application.walletCoveredAmountFen),
  );
  const applicationExternalFen = safeSum(
    result.ruleApplications.map((application) => application.externalAmountFen),
  );
  return (
    safeSum(result.entries.map((entry) => entry.coveredAmountFen)) ===
      result.walletCoveredAmountFen &&
    applicationAmountFen === result.amountFen &&
    applicationWalletFen === result.walletCoveredAmountFen &&
    applicationExternalFen === result.externalAmountFen &&
    result.amountFen === result.walletCoveredAmountFen + result.externalAmountFen &&
    refundableAmountFen === result.refundPolicy.refundableAmountFen &&
    nonRefundableAmountFen === result.refundPolicy.nonRefundableAmountFen &&
    refundableAmountFen + nonRefundableAmountFen === result.walletCoveredAmountFen
  );
}
